// src/hooks/useEmployee.js
//
// Singular employee hook for the detail/edit screen. Conditionally fetches
// the PII subcollection and merges it onto the basic doc at LEGACY paths,
// so consumers that dereference `emp.banking.accountName` or
// `emp.employment.dailySalary` keep working without code changes.
//
// Plural-list hook is `useEmployees` in the same directory — it reads only
// the basic collection (no PII fetch). Don't use this hook for list views.
//
// See PRD_UAM_CustomRoles2.md §5.4 (hook contract).

import { useEffect, useMemo, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "./useAuth";

/**
 * Fetch one employee with optional PII merge.
 *
 * - Always fetches the basic doc.
 * - Fetches the PII subcollection doc IFF the caller has `emp:pii:view`.
 * - Returns a merged object where PII fields are placed at their LEGACY
 *   paths (`employment.dailySalary`, `banking.accountName`, etc.) so
 *   existing components reading those paths keep working.
 *
 * @param {string} companyId
 * @param {string} empId - Employee document ID
 * @returns {{
 *   employee: object | null,
 *   hasPii: boolean,
 *   loading: boolean,
 *   error: Error | string | null,
 * }}
 */
export function useEmployee(companyId, empId) {
  const { hasPermission } = useAuth();

  const [basic, setBasic] = useState(null);
  const [pii, setPii] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!companyId || !empId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const basicRef = doc(db, "companies", companyId, "employees", empId);
    const piiRef = doc(basicRef, "pii", empId);

    const fetches = [getDoc(basicRef)];
    if (hasPermission("emp:pii:view")) {
      fetches.push(getDoc(piiRef));
    }

    Promise.all(fetches)
      .then(([basicSnap, piiSnap]) => {
        if (!basicSnap.exists()) {
          setError("Employee not found");
          setBasic(null);
          setPii(null);
          return;
        }
        setBasic({ id: basicSnap.id, ...basicSnap.data() });
        setPii(piiSnap?.exists() ? piiSnap.data() : null);
      })
      .catch((err) => {
        console.error("[useEmployee] fetch failed:", err);
        setError(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [companyId, empId, hasPermission]);

  /**
   * Merged shape — PII fields placed at LEGACY paths so consumer components
   * that read `emp.banking.accountName` or `emp.employment.dailySalary`
   * keep working without changes.
   *
   * Old (pre-migration) employees with PII fields on the parent doc and no
   * PII subcollection: merge returns the basic doc unchanged, fields read
   * from their original parent locations.
   *
   * New (post-v2.0) employees with PII subcollection: PII fields override
   * any same-named fields on the parent doc.
   */
  const employee = useMemo(() => {
    if (!basic) return null;
    if (!pii) return basic;
    return {
      ...basic,
      banking: pii.banking,
      governmentIds: pii.governmentIds,
      employment: { ...basic.employment, ...pii.compensation },
    };
  }, [basic, pii]);

  return {
    employee,
    hasPii: basic?.hasPii === true,
    loading,
    error,
  };
}
